import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { ApiError } from "../../api/client";
import { HierarchyApi } from "../hierarchy/api";
import { RbacApi, type Assignment } from "./api";

type Level = Assignment["scopeLevel"];

export function Assignments() {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<string>("");

  const users = useQuery({ queryKey: ["users"], queryFn: RbacApi.listUsers });
  const roles = useQuery({ queryKey: ["roles"], queryFn: RbacApi.listRoles });
  const positions = useQuery({ queryKey: ["positions"], queryFn: RbacApi.listPositions });
  const constituencies = useQuery({
    queryKey: ["constituencies"],
    queryFn: HierarchyApi.listConstituencies,
  });

  const assignments = useQuery({
    queryKey: ["assignments", selectedUser],
    queryFn: () => RbacApi.listAssignments(selectedUser),
    enabled: !!selectedUser,
  });

  const create = useMutation({
    mutationFn: RbacApi.createAssignment,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assignments", selectedUser] });
      qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Failed"),
  });
  const remove = useMutation({
    mutationFn: RbacApi.deleteAssignment,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assignments", selectedUser] });
      qc.invalidateQueries({ queryKey: ["users"] });
    },
  });

  return (
    <div className="hierarchy">
      <header>
        <h1>Assignments</h1>
        <p className="muted">Attach a role + scope to a user. Effective permissions update immediately.</p>
      </header>
      {error && <div role="alert" className="error">{error}</div>}

      <div className="row" style={{ background: "transparent", border: "none", padding: 0 }}>
        <label>
          User:&nbsp;
          <select value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)}>
            <option value="">— choose —</option>
            {users.data?.items.map((u) => (
              <option key={u.id} value={u.id}>{u.fullName} ({u.email})</option>
            ))}
          </select>
        </label>
      </div>

      {selectedUser && (
        <>
          <NewAssignmentForm
            userId={selectedUser}
            users={(users.data?.items ?? []).filter((u) => u.id !== selectedUser)}
            roles={roles.data?.items ?? []}
            positions={positions.data?.items ?? []}
            constituencies={constituencies.data?.items ?? []}
            onSubmit={(body) => create.mutate(body)}
            submitting={create.isPending}
          />

          <ul className="tree" style={{ marginTop: 16 }}>
            {assignments.data?.items.map((a) => (
              <li key={a.id}>
                <div className="row">
                  <span>
                    Role: <strong>{a.roleId}</strong> · Scope: <strong>{a.scopeLevel}/{a.scopeId}</strong>
                    {a.positionId && <> · Position: <strong>{a.positionId}</strong></>}
                    {a.reportsToUid && <> · Reports to: <code>{a.reportsToUid}</code></>}
                    {(a.startsAt || a.endsAt) && (
                      <div className="muted" style={{ fontSize: 12 }}>
                        {a.startsAt ?? "—"} → {a.endsAt ?? "indefinite"}
                      </div>
                    )}
                  </span>
                  <span className="row-actions">
                    <button
                      className="danger"
                      onClick={() => {
                        if (window.confirm("Revoke this assignment? The user will lose this role + scope immediately. The audit log keeps the record."))
                          remove.mutate(a.id);
                      }}
                    >Revoke</button>
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function NewAssignmentForm({
  userId,
  users,
  roles,
  positions,
  constituencies,
  onSubmit,
  submitting,
}: {
  userId: string;
  users: { id: string; fullName: string; email: string }[];
  roles: { id: string; name: string }[];
  positions: { id: string; name: string; scopeLevel: Level }[];
  constituencies: { id: string; name: string }[];
  onSubmit: (body: Omit<Assignment, "id">) => void;
  submitting: boolean;
}) {
  const [roleId, setRoleId] = useState("");
  const [positionId, setPositionId] = useState("");
  const [scopeLevel, setScopeLevel] = useState<Level>("branch");
  const [scopeId, setScopeId] = useState("");
  const [reportsToUid, setReportsToUid] = useState("");
  const [endsAt, setEndsAt] = useState("");

  const [parentCid, setParentCid] = useState("");
  const wards = useQuery({
    queryKey: ["wards", parentCid],
    queryFn: () => HierarchyApi.listWards(parentCid),
    enabled: !!parentCid,
  });
  const [parentWid, setParentWid] = useState("");
  const branches = useQuery({
    queryKey: ["branches", parentWid],
    queryFn: () => HierarchyApi.listBranches(parentWid),
    enabled: !!parentWid,
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!roleId || !scopeId) return;
    onSubmit({
      userId,
      roleId,
      positionId: positionId || null,
      scopeLevel,
      scopeId,
      reportsToUid: reportsToUid || null,
      startsAt: null,
      endsAt: endsAt || null,
    });
    setRoleId(""); setPositionId(""); setScopeId(""); setEndsAt(""); setReportsToUid("");
  }

  return (
    <form className="create-role" onSubmit={submit}>
      <div className="row-3">
        <label>
          Role:
          <select value={roleId} onChange={(e) => setRoleId(e.target.value)} required>
            <option value="">— choose —</option>
            {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </label>
        <label>
          Position:
          <select value={positionId} onChange={(e) => setPositionId(e.target.value)}>
            <option value="">(none)</option>
            {positions
              .filter((p) => p.scopeLevel === scopeLevel)
              .map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <label>
          Ends at:
          <input type="date" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
        </label>
      </div>

      <div className="row-3" style={{ marginTop: 8 }}>
        <label>
          Reports to:
          <select value={reportsToUid} onChange={(e) => setReportsToUid(e.target.value)}>
            <option value="">(no chain — top of hierarchy)</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.fullName} ({u.email})</option>
            ))}
          </select>
        </label>
        <span /><span />
      </div>

      <div className="row-3" style={{ marginTop: 8 }}>
        <label>
          Scope level:
          <select
            value={scopeLevel}
            onChange={(e) => {
              setScopeLevel(e.target.value as Level);
              setScopeId("");
            }}
          >
            <option value="constituency">Constituency</option>
            <option value="ward">Ward</option>
            <option value="branch">Branch</option>
          </select>
        </label>

        {scopeLevel === "constituency" && (
          <label>
            Constituency:
            <select value={scopeId} onChange={(e) => setScopeId(e.target.value)} required>
              <option value="">— choose —</option>
              {constituencies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
        )}

        {scopeLevel === "ward" && (
          <>
            <label>
              Constituency:
              <select value={parentCid} onChange={(e) => { setParentCid(e.target.value); setScopeId(""); }} required>
                <option value="">— choose —</option>
                {constituencies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label>
              Ward:
              <select value={scopeId} onChange={(e) => setScopeId(e.target.value)} required>
                <option value="">— choose —</option>
                {wards.data?.items.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </label>
          </>
        )}

        {scopeLevel === "branch" && (
          <>
            <label>
              Constituency:
              <select value={parentCid} onChange={(e) => { setParentCid(e.target.value); setParentWid(""); setScopeId(""); }} required>
                <option value="">— choose —</option>
                {constituencies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label>
              Ward:
              <select value={parentWid} onChange={(e) => { setParentWid(e.target.value); setScopeId(""); }} required>
                <option value="">— choose —</option>
                {wards.data?.items.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </label>
            <label>
              Branch:
              <select value={scopeId} onChange={(e) => setScopeId(e.target.value)} required>
                <option value="">— choose —</option>
                {branches.data?.items.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </label>
          </>
        )}
      </div>

      <div style={{ marginTop: 8 }}>
        <button type="submit" disabled={submitting}>
          {submitting ? "Creating…" : "Create assignment"}
        </button>
      </div>
    </form>
  );
}
